import type { VolumeProfileIncrement, VolumeProfileSnapshot } from '../snapshot';
import type { KiasiHistorySummaryRow } from 'src/lib/kiasi';
import type { VolumeBarSnapshot } from 'src/lib/kiasi/bars';
import type { LiveBarStatus } from 'src/services/KiasiLiveBarService';

export type VolumeProfileTabId = 'profile' | 'chart';
export type VolumeProfileViewMode = 'quote' | 'session' | 'total';
export type VolumeProfileSession = 'all' | 'premarket' | 'regular' | 'postmarket';
export type VolumeProfileRange = 'day' | '1h' | '5m' | '1m' | 'custom';
export type VolumeProfileOverlay = 'poc' | 'valueArea' | 'volumeProfile' | 'price' | 'vwap' | 'bidAsk';
export type { VolumeProfileIncrement } from '../snapshot';

/** What the loaded data belongs to; results for anything else are stale and ignored. */
export interface VolumeProfileSubject {
    readonly symbol: string;
    readonly range: VolumeProfileRange;
}

export type VolumeProfileHistoryStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Data the engines delivered for the current subject. The history query, the
 * summary query, and the live stream only dispatch into this; the reducer
 * decides what is shown.
 */
export interface VolumeProfileDataState {
    readonly subject: VolumeProfileSubject | null;
    readonly historyStatus: VolumeProfileHistoryStatus;
    readonly history: VolumeProfileSnapshot | null;
    readonly historyError: Error | null;
    readonly summaryRows: readonly KiasiHistorySummaryRow[] | null;
    readonly liveBar: VolumeBarSnapshot | null;
    readonly liveStatus: LiveBarStatus;
}

export interface VolumeProfileState {
    tabId: VolumeProfileTabId;
    viewMode: VolumeProfileViewMode;
    session: VolumeProfileSession;
    range: VolumeProfileRange;
    /** Histogram price increment in dollars, or 'auto' to fit the range. */
    increment: VolumeProfileIncrement;
    overlays: Readonly<Record<VolumeProfileOverlay, boolean>>;
    data: VolumeProfileDataState;
}

export interface VolumeProfileBarSegment {
    id: string;
    label: string;
    widthPct: number;
    className: string;
}

export interface VolumeProfileLevelView {
    id: string;
    price: number;
    priceFormatted: string;
    total: number;
    totalFormatted: string;
    segments: readonly VolumeProfileBarSegment[];
    isPoc: boolean;
    isVah: boolean;
    isVal: boolean;
}

export interface VolumeProfileStatRow {
    label: string;
    value: string;
    tone: 'default' | 'aqua' | 'jade' | 'ember' | 'honey' | 'purple';
    progressPct: number | null;
    progressClassName: string;
}

export interface VolumeProfileStatGroup {
    id: string;
    title: string;
    rows: readonly VolumeProfileStatRow[];
}

export interface VolumeProfileLegendItem {
    id: string;
    label: string;
    className: string;
}

export function createInitialVolumeProfileDataState(
    subject: VolumeProfileSubject | null = null,
): VolumeProfileDataState {
    return {
        subject,
        historyStatus: 'idle',
        history: null,
        historyError: null,
        summaryRows: null,
        liveBar: null,
        liveStatus: 'idle',
    };
}

export function createInitialVolumeProfileState(): VolumeProfileState {
    return {
        tabId: 'profile',
        viewMode: 'quote',
        session: 'all',
        range: 'day',
        increment: 'auto',
        overlays: {
            poc: true,
            valueArea: true,
            volumeProfile: true,
            price: true,
            vwap: true,
            bidAsk: true,
        },
        data: createInitialVolumeProfileDataState(),
    };
}
